import { useState } from 'react';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import { scanApi, ApiError } from '../services/api';
import type { ScanResult } from '../types';

interface DomainScannerProps {
  onScanComplete: (result: ScanResult) => void;
}

export default function DomainScanner({ onScanComplete }: DomainScannerProps) {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const cleanDomain = domain.trim().toLowerCase();
    if (!cleanDomain) {
      setError('Please enter a domain');
      return;
    }

    // Basic domain validation
    const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
    if (!domainRegex.test(cleanDomain)) {
      setError('Invalid domain format. Example: example.com');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await scanApi.scanDomain(cleanDomain);
      onScanComplete(result);
      setDomain('');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 402) {
          setError('Active subscription required. Please upgrade to Pro.');
        } else if (err.status === 403) {
          setError('This domain cannot be scanned (restricted).');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to scan domain. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="Enter domain to scan (e.g., example.com)"
            className="input-field w-full pl-12"
            disabled={loading}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary flex items-center gap-2 min-w-[140px] justify-center"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <Search size={18} />
              Scan Domain
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-signal-red text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}
